import { Ref, WatchStopHandle, computed, ref, watch } from "vue";
import { SourceConfig, SourcePlayback } from "./helpers/sourceHelper";
import { BackgroundConfig, BackgroundType } from "./helpers/backgroundHelper";
import { InputResolution, PipelineName, SegmentationConfig, SegmentationModel } from "./helpers/segmentationHelper";
import { PostProcessingConfig } from "./helpers/postProcessingHelper";
import useTFLite from "./hooks/useTFLite";
import useRenderingPipeline from "./hooks/useRenderingPipeline";
import { RenderingPipeline } from "./helpers/renderingPipelineHelper";
import { TimerWorker } from "./helpers/timerHelper";
import { InferenceWorker, useInferenceWorker } from "./hooks/useInferenceWorker";
// Type-only: the implementation is dynamically imported below so @mediapipe/tasks-vision stays out of
// the main bundle unless the MediaPipe backend is actually enabled.
import type { SegmenterHandle } from "./hooks/useImageSegmenter";

type BgBlurParams = {
  backgroundActive: Ref<boolean>;
  coverageMin: Ref<number>;
  coverageMax: Ref<number>;
  lightWrapping: Ref<number>;
  blendMode: Ref<"screen" | "linearDodge">;
  smoothSegmentationMask: Ref<boolean>;
  sigmaSpace: Ref<number>;
  sigmaColor: Ref<number>;
  scale: Ref<number>;
  pipeline: Ref<PipelineName>;
  blurAmount: Ref<number>;
  backgroundSegmentationModel: Ref<SegmentationModel>;
  backgroundOperation: Ref<BackgroundType>;
  backgroundUrl: Ref<string>;
  quality: Ref<"high" | "medium">;
  backgroundInferenceWorker: Ref<boolean>;
};

// Fleet-wide kill switch: set window.CONFIG.BG_INFERENCE_WORKER = false to force inline inference
// for a whole customer without editing per-scope background settings. Undefined => worker allowed.
function inferenceWorkerAllowed(params: BgBlurParams): boolean {
  const configAllowed = (window as any).CONFIG?.BG_INFERENCE_WORKER !== false;
  return params.backgroundInferenceWorker.value && configAllowed;
}

const inputResolutionsHigh: Map<string, InputResolution> = new Map<string, InputResolution>();
inputResolutionsHigh.set("mlkit", "256x256");
inputResolutionsHigh.set("meet", "256x144");

const inputResolutionsMedium: Map<string, InputResolution> = new Map<string, InputResolution>();
inputResolutionsMedium.set("mlkit", "256x256");
inputResolutionsMedium.set("meet", "160x96");

let modelsLoaded = false;

async function bgBlur(params: BgBlurParams) {
  if (!modelsLoaded) {
    modelsLoaded = true;

    console.log("Loading models...");
    //load tflite/tflite.js as script and wait for it to load
    console.log(`loading tflite.js`);
    const script = document.createElement("script");
    script.src = "/tflite/tflite.js";
    document.body.appendChild(script);
    await new Promise((resolve) => {
      script.onload = resolve;
    });

    // load tflite/tflite-simd.js as script and wait for it to load
    console.log(`loading tflite-simd.js`);
    const scriptSIMD = document.createElement("script");
    scriptSIMD.src = "/tflite/tflite-simd.js";
    document.body.appendChild(scriptSIMD);
    await new Promise((resolve) => {
      scriptSIMD.onload = resolve;
    });
  }

  let sourceConfig: SourceConfig = {
    type: "camera",
  };

  let backgroundConfig: BackgroundConfig = {
    type: params.backgroundOperation.value,
    url: params.backgroundUrl.value,
    blurAmount: params.blurAmount,
    scale: params.scale,
  };

  let inputResolutions = params.quality.value === "high" ? inputResolutionsHigh : inputResolutionsMedium;

  let segmentationConfig: SegmentationConfig = {
    model: params.backgroundSegmentationModel.value,
    backend: "wasmSimd",
    inputResolution: inputResolutions.get(params.backgroundSegmentationModel.value),
    pipeline: params.pipeline.value,
    // Camera delivers ~30fps; running the pipeline faster reprocesses duplicate frames and doubles
    // CPU/GPU for no visual gain. Configurable via CONFIG.BG_TARGET_FPS, safe default 30.
    targetFps: (window as any).CONFIG?.BG_TARGET_FPS ?? 30,
    deferInputResizing: true,
    inferenceWorker: inferenceWorkerAllowed(params),
  };

  function getPostProcessingConfig(): PostProcessingConfig {
    return {
      smoothSegmentationMask: params.smoothSegmentationMask.value,
      jointBilateralFilter: { sigmaSpace: params.sigmaSpace.value, sigmaColor: params.sigmaColor.value },
      coverage: [params.coverageMin.value, params.coverageMax.value],
      lightWrapping: params.lightWrapping.value,
      blendMode: params.blendMode.value,
    };
  }

  let { tflite, isSIMDSupported } = await useTFLite(segmentationConfig);

  if (segmentationConfig.backend === "wasm" && isSIMDSupported) {
    segmentationConfig = { ...segmentationConfig, backend: "wasmSimd" };
  }

  async function processVideo(source: SourcePlayback) {
    let tfliteResolved = tflite;

    let pipeline: RenderingPipeline;
    let backgroundImageRef: HTMLImageElement;
    let canvasRef: HTMLCanvasElement;
    let fps: Ref<number>;
    let durations: Ref<number[]>;
    let timerWorker: TimerWorker | null = null;

    let oldWatcher: WatchStopHandle | null = null;
    let localStream: MediaStream = null;

    let media_resolve: (_: any) => void;
    let promise = new Promise((resolve) => {
      media_resolve = resolve;
    });

    const stopMainWatch = watch(
      [params.pipeline, params.backgroundSegmentationModel, params.backgroundUrl, params.backgroundOperation, params.quality],
      async ([newPipeline, newbackgroundSegmentationModel], [oldPipeline, oldBackgroundModel]) => {
        if (!params.backgroundActive.value) {
          media_resolve("");
          return;
        }

        segmentationConfig.pipeline = newPipeline;
        segmentationConfig.model = newbackgroundSegmentationModel;
        inputResolutions = params.quality.value === "high" ? inputResolutionsHigh : inputResolutionsMedium;
        segmentationConfig.inputResolution = inputResolutions.get(newbackgroundSegmentationModel);
        backgroundConfig.type = params.backgroundOperation.value;
        backgroundConfig.url = params.backgroundUrl.value;
        backgroundConfig.blurAmount = params.blurAmount;
        backgroundConfig.scale = params.scale;

        if (oldBackgroundModel !== newbackgroundSegmentationModel) {
          let { tflite, isSIMDSupported } = await useTFLite(segmentationConfig);
          tfliteResolved = tflite;

          if (segmentationConfig.backend === "wasm" && isSIMDSupported) {
            segmentationConfig = { ...segmentationConfig, backend: "wasmSimd" };
          }
        }

        console.log(`Pipeline: ${segmentationConfig.pipeline}`);
        console.log(`Model: ${segmentationConfig.model}`);

        if (pipeline) {
          oldWatcher?.();
          timerWorker?.terminate();
          pipeline.cleanUp(); // also terminates the previous inference worker (passed into the pipeline)
        }

        // SPIKE: MediaPipe ImageSegmenter (GPU-delegate) backend, gated by CONFIG.BG_SEGMENTATION_BACKEND="mediapipe".
        // Replaces tflite entirely; falls back to tflite (worker/inline) on init failure. Default "tflite".
        const useMediaPipeBackend =
          segmentationConfig.pipeline === "webgl2" && (window as any).CONFIG?.BG_SEGMENTATION_BACKEND === "mediapipe";
        let imageSegmenterHandle: SegmenterHandle | null = null;
        if (useMediaPipeBackend) {
          try {
            const { useImageSegmenter } = await import("./hooks/useImageSegmenter");
            imageSegmenterHandle = await useImageSegmenter();
            // The pipeline sizes the MediaPipe segmentation textures to the frame resolution (the mask is
            // returned at video res), so no inputResolution alignment is needed here.
          } catch (e) {
            console.warn("BgBlur: MediaPipe ImageSegmenter init failed, falling back to tflite", e);
            imageSegmenterHandle = null;
          }
        }

        // tflite worker only when NOT using MediaPipe.
        segmentationConfig.inferenceWorker = !imageSegmenterHandle && inferenceWorkerAllowed(params);
        let inferenceWorkerHandle: InferenceWorker | null = null;
        if (!imageSegmenterHandle && segmentationConfig.pipeline === "webgl2" && segmentationConfig.inferenceWorker) {
          try {
            inferenceWorkerHandle = await useInferenceWorker(segmentationConfig, segmentationConfig.backend === "wasmSimd");
          } catch (e) {
            console.warn("BgBlur: inference worker init failed, falling back to inline inference", e);
            inferenceWorkerHandle = null;
          }
        }

        console.log(
          `BgBlur: inference running ${
            imageSegmenterHandle ? "in MEDIAPIPE (GPU delegate)" : inferenceWorkerHandle ? "in WORKER (off main thread)" : "INLINE (main thread)"
          } — backend=${(window as any).CONFIG?.BG_SEGMENTATION_BACKEND ?? "tflite"}, workerAllowed=${segmentationConfig.inferenceWorker}, ` +
            `pipeline=${segmentationConfig.pipeline}, CONFIG.BG_INFERENCE_WORKER=${(window as any).CONFIG?.BG_INFERENCE_WORKER}`,
        );

        const values = useRenderingPipeline(
          source,
          backgroundConfig,
          segmentationConfig,
          tfliteResolved,
          inferenceWorkerHandle,
          imageSegmenterHandle,
        );
        pipeline = values.pipeline;
        backgroundImageRef = values.backgroundImageRef;
        canvasRef = values.canvasRef;
        fps = values.fps;
        durations = values.durations;
        timerWorker = values.timerWorker;

        pipeline.updatePostProcessingConfig(getPostProcessingConfig());

        localStream = canvasRef.captureStream();
        // add all audio tracks from the original media stream
        for (const track of source.originalMediaStream.getAudioTracks()) {
          localStream.addTrack(track);
        }

        media_resolve("");

        oldWatcher = watch(
          () => getPostProcessingConfig(),
          (newConfig) => {
            pipeline.updatePostProcessingConfig(newConfig);
          },
        );
      },
      { immediate: true },
    );

    // Full teardown for this pipeline: stop the reactive watchers, the render loop / timer worker,
    // the inference worker, all GL resources, and release the raw camera/mic tracks that fed it.
    // Called by the module hook when media is reacquired (device change / resetMedia) or the call ends.
    let cleanedUp = false;
    function cleanUp() {
      if (cleanedUp) {
        return;
      }
      cleanedUp = true;
      stopMainWatch();
      oldWatcher?.();
      timerWorker?.terminate();
      pipeline?.cleanUp();
      source.originalMediaStream.getTracks().forEach((track) => track.stop());
    }

    const statDetails = computed(() => {
      return `
Resizing: ${durations.value[0].toFixed(2)}ms
Inference: ${durations.value[1].toFixed()}ms
Post-processing: ${durations.value[2].toFixed(2)}ms`;
    });

    await promise;

    if (!params.backgroundActive.value) {
      return { localStream: null, pipeline, backgroundImageRef, canvasRef, fps, statDetails, cleanUp };
    }

    localStream = canvasRef.captureStream();
    // add all audio tracks from the original media stream
    for (const track of source.originalMediaStream.getAudioTracks()) {
      localStream.addTrack(track);
    }

    return { localStream, pipeline, backgroundImageRef, canvasRef, fps, statDetails, cleanUp };
  }

  async function createSourcePlayback(media: MediaStream): Promise<SourcePlayback> {
    if (!sourcePlaybackElementCache) {
      sourcePlaybackElementCache = document.createElement("video");
      sourcePlaybackElementCache.autoplay = true;
      sourcePlaybackElementCache.muted = true;
      sourcePlaybackElementCache.playsInline = true;
      document.body.appendChild(sourcePlaybackElementCache);
    }

    sourcePlaybackElementCache.srcObject = media;

    await sourcePlaybackElementCache.play();

    const width = sourcePlaybackElementCache.videoWidth;
    const height = sourcePlaybackElementCache.videoHeight;

    return { htmlElement: sourcePlaybackElementCache, width, height, originalMediaStream: media };
  }

  return { sourceConfig, backgroundConfig, processVideo, createSourcePlayback };
}

let sourcePlaybackElementCache: HTMLVideoElement = null;

export default bgBlur;
